import {
    Directive,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { WindowRef } from '../../../vendor/window/window.component';
import { Subject, takeUntil } from 'rxjs';
import {
    PlaidError,
    PlaidErrorMetadata,
    PlaidEventMetadata,
    PlaidSuccessMetadata,
} from '@components/plaid-link/interfaces';
import { PlaidSuccessEvent } from '@components/plaid-link/plaid-success.event';
import { DialogService } from '@components/dialog/dialog.service';
import { ToastService } from '@core/state/toast/toast.service';
import { BillingService } from '@features/billing/billing.service';
import { $log } from '@core/utils/logger';

@Directive({ selector: '[ihdPlaidLink]' })
export class PlaidLinkDirective implements OnInit, OnDestroy {
    /**
     * Pass this input as true if you want the directive to also call `/put billing` and update the subscription
     * Originally this directive only returned the response from plaid but this is used in different places to avoid subscription
     * as well so to avoid replication this is now also responsible to updating subscription
     */
    @Input()
    updateSubscription?: boolean = false;

    @Output()
    connectSuccess: EventEmitter<PlaidSuccessEvent> = new EventEmitter<PlaidSuccessEvent>();

    @Output()
    connectError: EventEmitter<PlaidErrorMetadata> = new EventEmitter<PlaidErrorMetadata>();

    @Output()
    close: EventEmitter<PlaidEventMetadata> = new EventEmitter<PlaidEventMetadata>();

    /**
     * Only emitted if updateSubscription was passed as true and the update was successful
     */
    @Output()
    updateSuccess: EventEmitter<boolean> = new EventEmitter<boolean>();

    /**
     * Only emitted if updateSubscription was passed as true and the update failed
     */
    @Output()
    updateError: EventEmitter<boolean> = new EventEmitter<boolean>();

    @HostBinding('disabled')
    isLoading: boolean;

    private nativeWindow;
    private accountDetails;

    private destroyed$: Subject<void> = new Subject();
    private plaidHandler;
    private accountDetailsMetadata: PlaidSuccessMetadata;

    constructor(
        private dialogService: DialogService,
        private billingService: BillingService,
        private toastService: ToastService,
        private windowRef: WindowRef
    ) {
        this.nativeWindow = this.windowRef.nativeWindow;
    }

    @HostListener('click', ['$event'])
    onClick($event) {
        if (this.plaidHandler) {
            this.plaidHandler.open();
        }
    }

    ngOnInit() {
        this.billingService
            .fetchPlaidKey()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((response: any) => {
                $log.debug('response', response);

                this.plaidHandler = this.nativeWindow.Plaid.create({
                    token: response.billing_key?.token?.link_token,
                    onSuccess: (
                        publicToken: string,
                        metadata: PlaidSuccessMetadata
                    ) => {
                        $log.debug('public_token', publicToken, metadata);
                        this.connectSuccess.emit({
                            publicToken,
                            metadata,
                        });
                        this.accountDetailsMetadata = metadata;
                        if (this.updateSubscription) {
                            this.handleUpdate();
                        }
                    },
                    onLoad: () => {},
                    onExit: (err: PlaidError, metadata: PlaidErrorMetadata) => {
                        $log.debug('err, metadata', err, metadata);
                    },
                    onEvent: (eventName, metadata: PlaidEventMetadata) => {
                        $log.debug('eventName', eventName, metadata);
                        if (eventName === 'HANDOFF' || eventName === 'EXIT') {
                            this.close.emit(metadata);
                        }
                    },
                    receivedRedirectUri: null,
                });
            });
    }

    ngOnDestroy() {
        this.plaidHandler?.destroy();
        this.destroyed$.next();
    }

    private handleUpdate() {
        if (this.accountDetailsMetadata) {
            this.isLoading = true;

            let billingDetails: any = {
                plaid_source: this.accountDetailsMetadata.public_token,
            };

            if (this.accountDetailsMetadata.account) {
                billingDetails.plaid_account = this.accountDetailsMetadata.account.id;
            }

            this.billingService.updateBilling(billingDetails).subscribe(
                (response) => {
                    this.updateSuccess.emit(true);
                    this.dialogService.alert({
                        title: 'Billing.Subscription.Update.Success.Title',
                        content:
                            'Billing.Subscription.Update.Card.Success.Desc',
                        icon: 'check',
                        btnText: 'Common.Ok',
                    });
                },
                (response) => {
                    // ERROR: Update failed.
                    // Define payment gateway error as required
                    let billingError: any;
                    try {
                        billingError = JSON.parse(
                            response.payload.error.message
                        );
                    } catch (e) {
                        billingError = response.payload.error;
                    }

                    this.updateSuccess.emit(false);
                    this.isLoading = false;
                    this.toastService.showError(billingError.message);
                }
            );
        } else {
            // ERROR
            this.isLoading = false;
        }
    }
}
